import { Rows } from "../../../interfaces";
import { formats, getCell, withCellStyles } from "../../../utils";
import { getHeadersForDisplay, getHeadersForDisplayTitle, getHeadersForLogic } from "./headers";
import { getCommitmentAllocation } from "./mappingCommitmentAllocation";
import { getFundPerformance } from "./mappingFundPerformance";
import { FormArray, FormGroup } from "@angular/forms";
import { FundStrategyExportExcelData } from "../../../../models/fund-strategy/pojo/fund-strategy-export-classes";
import { FsGroupFormGroup, SubGroupFormGroup } from "../../../../models/fund-strategy/pojo/fund-strategy-export-types";
import {
  boldLightGrayBgLeft,
  centeredBoldGrayForHeaders,
  centerLinkStyle,
  left,
  leftIntend,
  textBold
} from "../../../excelStyles";

export const getPreparedData = (fullData: FundStrategyExportExcelData): Rows => {
  let newData: Rows = [];
  const metrics = fullData.metrics;
  // delete subgroups if no rounds
  const data = filterGroupsWithRounds(fullData.groups);

  onlyOneGroup = data.length === 1;
  // empty row for space between table and start of the sheet
  newData.push(emptyArrayForSpace);
  // define main tables indexes
  const mainRowsAndTotals = getMainRows(data);
  const grandTotalRow = onlyOneGroup ? [] : getGrandTotalRow();
  // collecting data for first table
  newData = getCommitmentAllocation(newData, metrics);
  // collecting data for second table
  newData = getFundPerformance(newData);
  // uses to define design for headers in table
  let designedHeaderRow = [];
  getHeadersForDisplayTitle().forEach((header) => designedHeaderRow.push(withCellStyles(header, centeredBoldGrayForHeaders)));
  newData.push(designedHeaderRow);
  designedHeaderRow = [];
  getHeadersForDisplay().forEach((header) => designedHeaderRow.push(withCellStyles(header, centeredBoldGrayForHeaders)));
  // push data
  newData.push(designedHeaderRow);
  newData.push(...mainRowsAndTotals);
  if (!onlyOneGroup) {
    newData.push(...grandTotalRow);
  }

  resetUtilsData();
  return newData;
};

const getMainRows = (data: FsGroupFormGroup[]): Rows => {
  const mainRowsAndTotal: Rows = [];
  const headers = getHeadersForLogic();
  data.forEach((group) => {
    let row = [];
    mainRowsAndTotal.push([withCellStyles(group.controls.name.value, textBold)]);
    currentRowIndex++;
    subTotalIndexs.push(currentRowIndex);
    const mappedToHeaders = group.controls.subGroups.controls.map((sub) => getNewSubGroupMapping(sub, group.controls.name.value));
    for (const mapped of mappedToHeaders) {
      row = [];
      for (const header of headers) {
        if (header === 'Name') {
          row.push(withCellStyles(mapped[header], leftIntend));
        } else if (header === '') {
          row.push(withCellStyles(mapped[header], centerLinkStyle));
        } else {
          row.push(withCellStyles(mapped[header], left));
        }
      }
      mainRowsAndTotal.push(row);
    }
    subTotalIndexs.push(currentRowIndex);
    const totalRow = getTotalRow();
    mainRowsAndTotal.push(...totalRow);
  });
  return mainRowsAndTotal;
};


const getTotalRow = (): Rows => {
  const rows: Rows = [];
  const headers = getHeadersForLogic();
  const mappedToHeaders = getNewSubTotalMapping();
  for (const mapped of mappedToHeaders) {
    const row = [];

    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getNewSubGroupMapping = (subGr: SubGroupFormGroup, groupName: string): object => {
  const oneRound = subGr.controls.roundsData.length === 1;
  groupName += ' ';
  const fullName = getUniqueSheetName(groupName + subGr.value.name);
  const body = {
    'Name': subGr.controls.name.value,
    '': getCell({
      v: subGr.controls.roundsData.length + (oneRound ? ' round' : ' rounds'),
      l: { Target: `#'${groupName + subGr.value.name}'!A1` }, // link to sheet
      t: 's',
    }),
    'empty': null,
    "Companies": getCell({
      f: generateLink('G', subGr, fullName),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Write-off": getCell({
      f: generateLink('O', subGr, fullName),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Exit": getCell({
      f: generateLink('K', subGr, fullName),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty(1)': null,
    "Investment": getCell({
      f: generateLink('Q', subGr, fullName),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Proceed": getCell({
      f: generateLink('R', subGr, fullName),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Gain/(loss)": getCell({
      f: `I${currentRowIndex}-H${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "MOIC": getCell({
      f: `IFERROR(I${currentRowIndex}/H${currentRowIndex},"-")`,
      z: formats.multiplier,
      t: 's',
    }),
    'Avg. check': getCell({
      f: `IFERROR(H${currentRowIndex}/D${currentRowIndex},"-")`,
      z: formats.thousandsSeparator,
      t: 's',
    }),
  };
  currentRowIndex++;
  return body;
};


const getNewSubTotalMapping = (): any => {
  const start = subTotalIndexs[0];
  const end = subTotalIndexs[1];
  const body = {
    Name: onlyOneGroup ? 'Total' : 'Sub total',
    '': '',
    'empty': null,
    "Companies": getCell({
      f: `SUM(D${start}:D${end - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Write-off": getCell({
      f: `SUM(E${start}:E${end - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Exit": getCell({
      f: `SUM(F${start}:F${end - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty(1)': null,
    "Investment": getCell({
      f: `SUM(H${start}:H${end - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Proceed": getCell({
      f: `SUM(I${start}:I${end - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Gain/(loss)": getCell({
      f: `I${currentRowIndex}-H${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "MOIC": getCell({
      f: `IFERROR(I${currentRowIndex}/H${currentRowIndex},"-")`,
      z: formats.multiplier,
      t: 's',
    }),
    'Avg. check': getCell({
      f: `IFERROR(H${currentRowIndex}/D${currentRowIndex},"-")`,
      z: formats.thousandsSeparator,
      t: 's',
    }),
  };
  forGrandTotalIndex.push(currentRowIndex);
  subTotalIndexs = [];
  if (onlyOneGroup) {
    singleGroupTotalIndex = currentRowIndex;
  }
  currentRowIndex++;
  return [body];
};

const getGrandTotalRow = (): Rows => {
  const rows: Rows = [];
  const headers = getHeadersForLogic();
  const mappedToHeaders = getGrandTotalMapping();
  for (const mapped of mappedToHeaders) {
    const row = [];

    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getGrandTotalMapping = (): any => {
  const body = {
    Name: 'Grand total',
    '': '',
    'empty': null,
    "Companies": getCell({
      f: generateSumFormulaForTotals('D'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Write-off": getCell({
      f: generateSumFormulaForTotals('E'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Exit": getCell({
      f: generateSumFormulaForTotals('F'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty(1)': null,
    "Investment": getCell({
      f: generateSumFormulaForTotals('H'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Proceed": getCell({
      f: generateSumFormulaForTotals('I'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Gain/(loss)": getCell({
      f: `I${currentRowIndex}-H${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "MOIC": getCell({
      f: `IFERROR(I${currentRowIndex}/H${currentRowIndex},"-")`,
      z: formats.multiplier,
      t: 's',
    }),
    'Avg. check': getCell({
      f: `IFERROR(H${currentRowIndex}/D${currentRowIndex},"-")`,
      z: formats.thousandsSeparator,
      t: 's',
    }),
  };
  multipleGroupGrandTotalRowIndex = currentRowIndex;
  return [body];
};

const resetUtilsData = (): void => {
  forGrandTotalIndex = [];
  subTotalIndexs = [];
  currentRowIndex = 26;
  singleGroupTotalIndex = undefined;
  multipleGroupGrandTotalRowIndex = undefined;
  uniqueSheetNames = new Set<string>();
};

const generateSumFormulaForTotals = (letter: string): string => {
  const columnReference = letter;
  return `SUM(${forGrandTotalIndex.map((index) => `${columnReference}${index}`).join(',')})`;
};


const generateLink = (letter: string, subGr: SubGroupFormGroup, name: string): string => {
  const contForAddingToRoundLen = 2;
  return `'${(name)}'!${letter}${subGr.value.roundsData.length + contForAddingToRoundLen}`;
};

const getUniqueSheetName = (base: string, i = 0): string => {
  // excel sheet allows only 31 letter so need to cut string to 29 letter and 2 letter for numbers if needs
  base = truncateName(base);
  const nameToCheck = i === 0 ? base : `${base}${i}`;

  if (uniqueSheetNames.has(nameToCheck)) {
    return getUniqueSheetName(base, i + 1);
  }

  uniqueSheetNames.add(nameToCheck);
  return nameToCheck;
};

const truncateName = (name: string, maxLength = 29): string => {
  return name.length > maxLength ? name.slice(0, maxLength) : name;
};

const filterGroupsWithRounds = (groups: FsGroupFormGroup[]): FsGroupFormGroup[] => {
  return groups
    .map((group) => {
      const validSubGroups = group.controls.subGroups.controls.filter(
        (subGroup) =>
          Array.isArray(subGroup.controls.roundsData.value) &&
          subGroup.controls.roundsData.value.length > 0
      );

      if (validSubGroups.length === 0) return null;

      const newSubGroupsArray = new FormArray<SubGroupFormGroup>(validSubGroups);

      const newGroup = new FormGroup({
        uuid: group.controls.uuid,
        name: group.controls.name,
        grOrder: group.controls.grOrder,
        subGroups: newSubGroupsArray,
      }) as FsGroupFormGroup;

      return newGroup;
    })
    .filter((g): g is FsGroupFormGroup => g !== null);
};

let uniqueSheetNames = new Set<string>();


let currentRowIndex = 26;
// save values of start end stop indexs for subtotals where [0] is the start row index, [1] is the end row index
export let subTotalIndexs = [];
export let singleGroupTotalIndex;
export let multipleGroupGrandTotalRowIndex;
// save values of subtotals for counting grand total
export let forGrandTotalIndex = [];
export let onlyOneGroup: boolean;
export const emptyArrayForSpace = [''];
